import { z } from "zod";
import { siteLanguagesSchema, siteLocaleSchema, validateLanguageSelection } from "./site-languages";
import { brandProfileSchema, type BrandProfile } from "./business";
import { suggestedPageIds } from "./brand-pages";

export const brandPromptRequestSchema = z.object({
  prompt: z.string().trim().min(20).max(6000),
  colours: z.string().trim().max(300).default(""),
  websiteStyle: z.string().trim().max(800).default(""),
  locale: z.enum(["el", "en"]),
  languages: siteLanguagesSchema.default(["el", "en"]),
  defaultLocale: siteLocaleSchema.optional()
}).strict().superRefine(validateLanguageSelection);
export type BrandPromptRequest = z.input<typeof brandPromptRequestSchema>;

// All fields are required for structured output. Unknown business facts stay blank.
const copy = (max: number) => z.string().max(max);
export const brandPromptSuggestionSchema = z.object({
  businessName: copy(100), businessType: copy(100), description: copy(1500),
  location: copy(160), serviceArea: copy(240), services: copy(1500), targetAudience: copy(600),
  phone: copy(50), email: copy(200), openingHours: copy(240),
  tone: z.enum(["friendly", "professional", "warm", "confident", "minimal", "playful"]),
  voiceNotes: copy(500), sellingPoints: copy(800), avoid: copy(800),
  goal: z.enum(["contact", "call", "email", "visit"]),
  palette: z.enum(["aegean", "mediterranean", "studio"]),
  primaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/), accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  visualStyle: z.enum(["minimal", "modern", "warm", "bold", "glass"]),
  headingFont: z.enum(["Manrope", "Playfair Display", "DM Sans"]),
  websiteFeel: copy(800), websiteNotes: copy(1500), offeringDetails: copy(2000),
  teamDescription: copy(1500), processDetails: copy(1500), workDetails: copy(1500), faqDetails: copy(2000),
  pages: z.array(z.object({
    title: z.string().min(1).max(100), slug: z.string().min(1).max(100).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    template: z.enum([...suggestedPageIds, "custom"]), summary: copy(900),
    showInNavigation: z.boolean(), showInFooter: z.boolean()
  }).strict()).min(1).max(10)
}).strict();
export type BrandPromptSuggestion = z.infer<typeof brandPromptSuggestionSchema>;
export const brandPromptResultSchema = z.object({ profile: brandProfileSchema }).strict();

/** Logos and other existing owner data do not go through the text model. */
export function applyBrandPrompt(profile: BrandProfile, suggestion: BrandProfile): BrandProfile {
  const merged = { ...profile, ...suggestion };
  for (const [key, value] of Object.entries(suggestion)) {
    if (value === "" && typeof profile[key as keyof BrandProfile] === "string") Object.assign(merged, { [key]: profile[key as keyof BrandProfile] });
  }
  return brandProfileSchema.parse({
    ...merged, contactDelivery: profile.contactDelivery, logoUrl: profile.logoUrl, logoAssetId: profile.logoAssetId, media: profile.media,
    address: profile.address, publishAddress: profile.publishAddress,
    showLocation: profile.showLocation, mapsUrl: profile.mapsUrl, mapEmbedUrl: profile.mapEmbedUrl,
    googlePlaceId: profile.googlePlaceId, googleReviewsEnabled: profile.googleReviewsEnabled,
    googleReviewsConsent: profile.googleReviewsConsent,
    facebook: profile.facebook, instagram: profile.instagram, linkedin: profile.linkedin, tiktok: profile.tiktok
  });
}

export function isEmptyBrandProfile(profile: BrandProfile): boolean {
  return ![profile.businessName, profile.businessType, profile.description, profile.services,
    profile.location, profile.serviceArea, profile.targetAudience, profile.websiteFeel,
    profile.websiteNotes, profile.offeringDetails, profile.teamDescription].some(value => value.trim())
    && !profile.websiteImport && !profile.customPages.length;
}
